import { useEffect, useState } from "react";
import { api, MODELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Compass, Sparkles, Loader2, Trophy, Award, ExternalLink, Target, TrendingUp, Printer, Upload } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/lib/api";

function uploadForFile(url, file, onDone, onErr, extraFields = {}) {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(extraFields).forEach(([k,v]) => fd.append(k, v));
  fetch(`${API}${url}`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("gf_token")}` }, body: fd })
    .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || "Failed"); return r.json(); })
    .then(onDone).catch(onErr);
}

export default function Discovery() {
  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="overline">Coach</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><Target className="w-10 h-10 text-primary"/>Discovery Coach</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Three deep-analysis tools: auto-pick the best funder for your proposal, get a reviewer-readiness score with ranked fixes, and find genuine gaps in the literature.</p>
        </div>
        <Button variant="outline" className="rounded-full print:hidden" onClick={()=>window.print()} data-testid="disc-print"><Printer className="w-4 h-4 mr-2"/>Print</Button>
      </div>
      <Tabs defaultValue="autopilot">
        <TabsList>
          <TabsTrigger value="autopilot" data-testid="disc-tab-autopilot"><Compass className="w-4 h-4 mr-1"/>Funder Autopilot</TabsTrigger>
          <TabsTrigger value="score" data-testid="disc-tab-score"><Trophy className="w-4 h-4 mr-1"/>Reviewer Score</TabsTrigger>
          <TabsTrigger value="gap" data-testid="disc-tab-gap"><TrendingUp className="w-4 h-4 mr-1"/>Literature Gap</TabsTrigger>
        </TabsList>
        <TabsContent value="autopilot"><Autopilot/></TabsContent>
        <TabsContent value="score"><ReviewerScore/></TabsContent>
        <TabsContent value="gap"><LiteratureGap/></TabsContent>
      </Tabs>
    </div>
  );
}

function useProposals() {
  const [proposals, setProposals] = useState([]);
  useEffect(() => { api.get("/proposals").then(r => setProposals(r.data)); }, []);
  return proposals;
}

function Autopilot() {
  const proposals = useProposals();
  const [pid, setPid] = useState(""); const [busy, setBusy] = useState(false); const [res, setRes] = useState(null);
  const run = async () => {
    if (!pid) return toast.error("Pick a proposal");
    setBusy(true); setRes(null);
    try { const { data } = await api.post(`/proposals/${pid}/autodetect-funder`, { proposal_id: pid }); setRes(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); }
    finally { setBusy(false); }
  };
  const runFile = (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    uploadForFile("/discovery/autopilot-file", file,
      (data)=>{ setRes(data); setBusy(false); toast.success("Analysed uploaded file"); },
      (err)=>{ setBusy(false); toast.error(err.message || "Upload failed"); });
  };
  return (
    <div className="mt-6 space-y-5">
      <div className="card-crisp rounded-lg p-5 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]"><Label>Proposal</Label>
          <Select value={pid} onValueChange={setPid}><SelectTrigger data-testid="ap-proposal"><SelectValue placeholder="Choose one of your proposals"/></SelectTrigger>
            <SelectContent>{proposals.map(p=><SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={busy} className="rounded-full" data-testid="ap-run">{busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Analysing…</> : <><Sparkles className="w-4 h-4 mr-2"/>Auto-detect funder</>}</Button>
        <div className="text-xs text-muted-foreground">or</div>
        <label className="inline-flex items-center gap-2 rounded-full border border-primary/40 px-4 py-2 text-sm hover:bg-primary/5 cursor-pointer" data-testid="ap-upload">
          <Upload className="w-4 h-4"/>Upload proposal
          <input type="file" accept=".pdf,.docx,.txt,.md" hidden onChange={(e)=>runFile(e.target.files?.[0])} data-testid="ap-upload-input"/>
        </label>
      </div>
      {res && (
        <motion.div initial={{opacity:0, y:6}} animate={{opacity:1, y:0}} className="space-y-3" data-testid="ap-result">
          {(res.picks||[]).map((p,i)=>(
            <div key={i} className="card-crisp rounded-lg p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="overline">Pick #{i+1}</div>
                  <div className="font-display text-2xl mt-1">{p.scheme}</div>
                  <div className="text-sm text-muted-foreground mt-1">{p.why}</div>
                </div>
                <div className="rounded-md border border-primary p-2 bg-primary/5 text-center min-w-[80px]">
                  <div className="overline">Fit</div>
                  <div className="font-display text-3xl text-primary" style={{fontFeatureSettings:'"lnum" 1'}}>{p.fit_score}</div>
                </div>
              </div>
              {p.compliance?.notes && <div className="mt-3 text-xs italic border-l-2 border-primary/40 pl-2">{p.compliance.notes}</div>}
              <div className="mt-3 grid md:grid-cols-3 gap-3 text-sm">
                <div><div className="overline">Budget heads</div><ul className="mt-1 space-y-0.5">{(p.budget_heads||[]).map((x,j)=><li key={j}>• {x}</li>)}</ul></div>
                <div><div className="overline">Must include</div><ul className="mt-1 space-y-0.5">{(p.must_include_sections||[]).map((x,j)=><li key={j}>• {x}</li>)}</ul></div>
                <div><div className="overline text-red-700">Red flags</div><ul className="mt-1 space-y-0.5">{(p.red_flags||[]).map((x,j)=><li key={j} className="text-red-800">• {x}</li>)}</ul></div>
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

function ReviewerScore() {
  const proposals = useProposals();
  const [pid, setPid] = useState(""); const [busy, setBusy] = useState(false); const [res, setRes] = useState(null);
  const run = async () => {
    if (!pid) return toast.error("Pick a proposal");
    setBusy(true); setRes(null);
    try { const { data } = await api.post(`/proposals/${pid}/reviewer-score`, { proposal_id: pid }); setRes(data); }
    catch { toast.error("Scoring failed"); }
    finally { setBusy(false); }
  };
  const runFile = (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    uploadForFile("/discovery/reviewer-score-file", file,
      (data)=>{ setRes(data); setBusy(false); toast.success("Scored uploaded file"); },
      (err)=>{ setBusy(false); toast.error(err.message || "Upload failed"); });
  };
  const impCol = { high:"bg-emerald-100 text-emerald-900", med:"bg-amber-100 text-amber-900", low:"bg-secondary" };
  return (
    <div className="mt-6 space-y-5">
      <div className="card-crisp rounded-lg p-5 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]"><Label>Proposal</Label>
          <Select value={pid} onValueChange={setPid}><SelectTrigger data-testid="rs-proposal"><SelectValue placeholder="Choose a proposal"/></SelectTrigger>
            <SelectContent>{proposals.map(p=><SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={busy} className="rounded-full" data-testid="rs-run">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Award className="w-4 h-4 mr-2"/>Score readiness</>}</Button>
        <div className="text-xs text-muted-foreground">or</div>
        <label className="inline-flex items-center gap-2 rounded-full border border-primary/40 px-4 py-2 text-sm hover:bg-primary/5 cursor-pointer" data-testid="rs-upload">
          <Upload className="w-4 h-4"/>Upload proposal
          <input type="file" accept=".pdf,.docx,.txt,.md" hidden onChange={(e)=>runFile(e.target.files?.[0])} data-testid="rs-upload-input"/>
        </label>
      </div>
      {res && (
        <div className="space-y-4" data-testid="rs-result">
          <div className="card-crisp rounded-lg p-6 flex items-center gap-6">
            <div className="w-32 h-32 relative">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="46" strokeWidth="8" className="stroke-secondary" fill="none"/>
                <circle cx="50" cy="50" r="46" strokeWidth="8" fill="none" className={res.overall>=75?"text-emerald-600":res.overall>=55?"text-amber-600":"text-red-600"} stroke="currentColor" strokeDasharray={`${(res.overall/100)*289} 289`} strokeLinecap="round"/>
              </svg>
              <div className={`absolute inset-0 flex flex-col items-center justify-center ${res.overall>=75?"text-emerald-600":res.overall>=55?"text-amber-600":"text-red-600"}`} style={{fontFeatureSettings:'"lnum" 1'}}>
                <div className="font-display text-4xl">{res.overall}</div><div className="text-[10px] tracking-widest uppercase">/ 100</div>
              </div>
            </div>
            <div className="flex-1">
              <div className="overline">Reviewer-readiness</div>
              <div className="font-display text-2xl mt-1">{res.one_line_verdict}</div>
              {res.top_3_priorities?.length>0 && <ul className="mt-3 space-y-1 text-sm">{res.top_3_priorities.map((s,i)=><li key={i}>⭐ {s}</li>)}</ul>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(res.criteria||[]).map((c,i)=>(
              <div key={i} className="card-crisp rounded-lg p-5">
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-lg capitalize">{c.name.replace(/_/g,' ')}</div>
                  <div className={`font-display text-3xl ${c.score>=75?"text-emerald-700":c.score>=55?"text-amber-700":"text-red-700"}`} style={{fontFeatureSettings:'"lnum" 1'}}>{c.score}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{c.why}</div>
                {c.ranked_fixes?.length>0 && (
                  <div className="mt-3 space-y-1.5">{c.ranked_fixes.map((f,j)=>(
                    <div key={j} className="text-xs flex items-start gap-2 border-l-2 border-primary/40 pl-2">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${impCol[f.impact]||"bg-secondary"}`}>{f.impact}</span>
                      <span>{f.fix}</span>
                    </div>
                  ))}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LiteratureGap() {
  const [aims, setAims] = useState(""); const [busy, setBusy] = useState(false); const [res, setRes] = useState(null); const [limit, setLimit] = useState(12);
  const run = async () => {
    if (aims.trim().length < 40) return toast.error("Paste at least 40 characters of aims");
    setBusy(true); setRes(null);
    const tid = toast.loading("Searching Semantic Scholar & CORE, then synthesising…");
    try { const { data } = await api.post("/literature/gap", { aims, limit }); setRes(data); toast.dismiss(tid); toast.success(`${data.papers?.length||0} papers analysed`); }
    catch (e) { toast.dismiss(tid); toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 space-y-5">
      <div className="card-crisp rounded-lg p-5 space-y-3">
        <div><Label>Research aims / objectives</Label><Textarea className="mt-2 min-h-[120px]" value={aims} onChange={(e)=>setAims(e.target.value)} placeholder="Paste your aims and objectives (or the abstract)…" spellCheck data-testid="gap-aims"/></div>
        <div className="flex gap-3 items-end">
          <div><Label>Papers to analyse</Label><Input type="number" min={5} max={20} value={limit} onChange={(e)=>setLimit(Number(e.target.value)||12)} className="w-24" data-testid="gap-limit"/></div>
          <Button onClick={run} disabled={busy} className="rounded-full ml-auto" data-testid="gap-run">{busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Analysing…</> : <><Sparkles className="w-4 h-4 mr-2"/>Find gaps</>}</Button>
        </div>
      </div>
      {res && (
        <div className="space-y-4" data-testid="gap-result">
          {res.papers && res.papers.length === 0 && (
            <div className="card-crisp rounded-lg p-4 border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="gap-empty-sources">
              Literature indexes were unavailable just now — gaps below are synthesised from your aims only. Try again in a minute for grounded citations.
            </div>
          )}
          {res.positioning_paragraph && (
            <div className="card-crisp rounded-lg p-5 border-primary/40 bg-primary/5">
              <div className="font-display text-2xl mb-2">Drop-in Significance paragraph</div>
              <p className="text-sm leading-relaxed">{res.positioning_paragraph}</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="card-crisp rounded-lg p-5">
              <div className="font-display text-2xl mb-2">Covered ground</div>
              <div className="space-y-3 text-sm">{(res.covered_ground||[]).map((c,i)=>(<div key={i}><div className="font-medium">{c.theme}</div><ul className="text-xs text-muted-foreground list-disc pl-5 mt-0.5">{(c.evidence||[]).map((e,j)=><li key={j}>{e}</li>)}</ul></div>))}</div>
            </div>
            <div className="card-crisp rounded-lg p-5 border-emerald-300 bg-emerald-50">
              <div className="font-display text-2xl mb-2 text-emerald-900">Genuine gaps</div>
              <div className="space-y-3 text-sm">{(res.gaps||[]).map((g,i)=>(<div key={i}><div className="font-medium">{g.gap} <span className="text-xs px-1.5 rounded-full bg-white ml-1 text-emerald-900">{g.difficulty}</span></div><div className="text-xs text-emerald-950/80">{g.why_novel}</div>{g.methods_hint && <div className="text-xs italic mt-0.5">→ {g.methods_hint}</div>}</div>))}</div>
            </div>
          </div>
          {res.papers?.length>0 && (
            <div className="card-crisp rounded-lg p-5">
              <div className="font-display text-2xl mb-2">Sources</div>
              <ol className="space-y-2 text-sm list-decimal pl-6">{res.papers.map((p,i)=>(
                <li key={i}><b>{p.title}</b> <span className="text-xs text-muted-foreground">· {p.year || "n.d."} · {p.source}</span>{p.url && <> · <a href={p.url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">open <ExternalLink className="w-3 h-3"/></a></>}</li>
              ))}</ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
